import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { useApp } from '@/app/AppContext'
import { visibleNavigation, visibleTopItems, type NavItem, type NavSection } from '@/app/navigation'
import { logout, restartLogin, userManager } from '@/auth/oidc'
import { syncBrowserPush } from '@/notifications/browserPush'
import { SigningOut } from '@/auth/AuthScreens'
import { fetchNotifications } from '@/notifications/api'
import { can } from '@/auth/permissions'
import { IncomingCallPopup } from '@/telephony/IncomingCallPopup'
import { CommandPalette, openPalette } from './CommandPalette'
import { Icon, SECTION_ICONS } from './Icon'
import type { ThemePreference } from '@/app/theme'
import { NAV_DRAWER_QUERY, useMediaQuery } from '@/app/viewport'

const COLLAPSED_KEY = 'crm.side.collapsed'
const CLOSED_GROUPS_KEY = 'crm.side.closedGroups'

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw == null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Приватный режим или запрет хранилища: меню просто не запомнит состояние.
  }
}

/**
 * Каркас приложения по дизайн-системе (.shell / .side / .topbar, styles/crm.css).
 *
 * Меню собирается из той же навигации, что палитра команд, и теми же правами: пункт
 * без права не рендерится, группа без пунктов исчезает целиком (правило дизайна и #47).
 */
export function AppLayout() {
  const { user, t } = useApp()
  const sections = visibleNavigation(user)
  const top = visibleTopItems(user)
  const [signingOut, setSigningOut] = useState(false)
  const [logoutFailed, setLogoutFailed] = useState(false)
  const [collapsed, setCollapsed] = useState(() => readStored(COLLAPSED_KEY, false))
  // Narrow screen (#109): the sidebar would take the whole width or push the page half a
  // screen down, so it becomes a drawer opened from the top bar.
  const narrow = useMediaQuery(NAV_DRAWER_QUERY)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const drawer = useRef<HTMLElement>(null)

  const closeDrawer = useCallback((restoreFocus: boolean) => {
    setDrawerOpen(false)
    // Focus returns to the button that opened the menu: otherwise it drops to <body>, and a
    // keyboard or screen reader user starts again from the top of the page.
    if (restoreFocus) menuButton.current?.focus()
  }, [])

  // A drawer left open while the window widens would sit on top of the desktop sidebar.
  useEffect(() => {
    if (!narrow) setDrawerOpen(false)
  }, [narrow])

  useEffect(() => {
    if (!drawerOpen) return
    drawer.current?.querySelector<HTMLElement>('a, button')?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') closeDrawer(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen, closeDrawer])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((was) => {
      store(COLLAPSED_KEY, !was)
      return !was
    })
  }, [])

  // Подписка браузера на push меняется сама, а service worker не может сообщить серверу
  // новый адрес — у него нет токена. Повтор регистрации при входе это и закрывает (#120).
  useEffect(() => {
    void syncBrowserPush()
  }, [])

  // «[» сворачивает меню — горячая клавиша из дизайна. Не в поле ввода: там это символ.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT' || target.isContentEditable === true)
      // No folding on a narrow screen: there the menu is a drawer, and "[" would silently
      // change a setting the user cannot see until the window is wide again.
      if (!typing && !narrow && event.key === '[' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        toggleCollapsed()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleCollapsed, narrow])

  if (signingOut) {
    return (
      <SigningOut
        failed={logoutFailed}
        onRetry={() => {
          void restartLogin().catch(() => setLogoutFailed(true))
        }}
      />
    )
  }

  return (
    <div className="shell">
      {!narrow && (
        <aside className="shell-side">
          {collapsed
            ? <CollapsedSidebar top={top} sections={sections} onExpand={toggleCollapsed} />
            : <Sidebar top={top} sections={sections} onCollapse={toggleCollapsed} />}
        </aside>
      )}
      {narrow && drawerOpen && (
        <>
          <div className="nav-backdrop" aria-hidden="true" onClick={() => closeDrawer(false)} />
          <aside
            id="nav-drawer"
            ref={drawer}
            className="shell-side nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.sections')}
          >
            {/* Choosing an item closes the drawer: the page it opens is what the user came
                for, and a menu still covering it would need a second tap to dismiss. */}
            <Sidebar
              top={top}
              sections={sections}
              onNavigate={() => closeDrawer(false)}
              onClose={() => closeDrawer(true)}
            />
          </aside>
        </>
      )}

      <div className="shell-main">
        <Topbar
          narrow={narrow}
          menuOpen={drawerOpen}
          menuButton={menuButton}
          onMenu={() => setDrawerOpen((was) => !was)}
          onSignOut={() => {
          setSigningOut(true)
          void logout().catch(() => setLogoutFailed(true))
        }} />
        <main className="page">
          <Outlet />
        </main>
      </div>

      <CommandPalette />
      {/* Incoming calls (#122): on every screen, since the phone rings whatever is open. */}
      <IncomingCallPopup />
    </div>
  )
}

interface SidebarProps {
  top: NavItem[]
  sections: NavSection[]
  /** Desktop: fold the menu into icons. */
  onCollapse?: () => void
  /** Drawer: an item was chosen. */
  onNavigate?: () => void
  /** Drawer: close without choosing. */
  onClose?: () => void
}

function Sidebar({ top, sections, onCollapse, onNavigate, onClose }: SidebarProps) {
  const { t } = useApp()
  const location = useLocation()
  const [closed, setClosed] = useState<string[]>(() => readStored(CLOSED_GROUPS_KEY, []))

  function toggle(key: string) {
    setClosed((was) => {
      const next = was.includes(key) ? was.filter((k) => k !== key) : [...was, key]
      store(CLOSED_GROUPS_KEY, next)
      return next
    })
  }

  return (
    <nav className="side" aria-label={t('nav.sections')}>
      <div className="side-brand">
        <span className="logo-ph" aria-hidden="true">CRM</span>
        <span>{t('app.title')}</span>
      </div>
      {/* Пункты над группами («Мой день», макет Sidebar): стартовый экран, а не раздел. */}
      {top.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={({ isActive }) => (isActive ? 'nav-item top is-active' : 'nav-item top')}
        >
          <Icon name="sun" />
          <span className="ellipsis">{t(item.labelKey)}</span>
        </NavLink>
      ))}
      {sections.map((section) => {
        // Группа с открытым сейчас разделом раскрыта всегда: иначе текущая страница
        // пропадает из меню, и непонятно, где ты.
        const hasActive = section.items.some((item) => location.pathname.startsWith(item.path))
        const open = hasActive || !closed.includes(section.titleKey)
        return (
          <div key={section.titleKey} className="stack" style={{ gap: 1 }}>
            <button
              className="nav-group"
              type="button"
              aria-expanded={open}
              onClick={() => toggle(section.titleKey)}
            >
              <Icon name={SECTION_ICONS[section.titleKey] ?? 'work'} />
              <span>{t(section.titleKey)}</span>
              <Icon name="chevron" className="chev" />
            </button>
            {open && section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={({ isActive }) => (isActive ? 'nav-item is-active' : 'nav-item')}
              >
                <span className="ellipsis">{t(item.labelKey)}</span>
              </NavLink>
            ))}
          </div>
        )
      })}
      <div className="side-foot">
        {onClose ? (
          <button className="btn btn-ghost btn-sm" type="button" style={{ flex: 1, justifyContent: 'flex-start' }} onClick={onClose}>
            <Icon name="close" size="sm" />
            {t('nav.closeMenu')}
          </button>
        ) : (
          <button className="btn btn-ghost btn-sm" type="button" style={{ flex: 1, justifyContent: 'flex-start' }} onClick={onCollapse}>
            <Icon name="sidebar-collapse" size="sm" />
            {t('nav.collapse')}
            <span className="kbd" style={{ marginLeft: 'auto' }}>[</span>
          </button>
        )}
      </div>
    </nav>
  )
}

function CollapsedSidebar({ top, sections, onExpand }: { top: NavItem[]; sections: NavSection[]; onExpand: () => void }) {
  const { t } = useApp()
  const location = useLocation()
  return (
    <nav className="side is-collapsed" aria-label={t('nav.sections')}>
      <div className="side-brand" style={{ padding: 0, justifyContent: 'center' }}>
        <span className="logo-ph" aria-hidden="true">CRM</span>
      </div>
      {top.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) => (isActive ? 'side-ico is-active' : 'side-ico')}
          aria-label={t(item.labelKey)}
          title={t(item.labelKey)}
        >
          <Icon name="sun" />
        </NavLink>
      ))}
      {sections.map((section) => {
        const first = section.items[0]
        if (!first) return null
        const active = section.items.some((item) => location.pathname.startsWith(item.path))
        return (
          <NavLink
            key={section.titleKey}
            to={first.path}
            className={active ? 'side-ico is-active' : 'side-ico'}
            aria-label={t(section.titleKey)}
            title={t(section.titleKey)}
          >
            <Icon name={SECTION_ICONS[section.titleKey] ?? 'work'} />
          </NavLink>
        )
      })}
      <div className="side-foot" style={{ width: 40, justifyContent: 'center' }}>
        <button className="btn btn-ghost btn-sm btn-icon" type="button" aria-label={t('nav.expand')} onClick={onExpand}>
          <Icon name="sidebar-expand" size="sm" />
        </button>
      </div>
    </nav>
  )
}

interface TopbarProps {
  narrow: boolean
  menuOpen: boolean
  menuButton: RefObject<HTMLButtonElement | null>
  onMenu: () => void
  onSignOut: () => void
}

function Topbar({ narrow, menuOpen, menuButton, onMenu, onSignOut }: TopbarProps) {
  const { user, locale, setLocale, theme, setTheme, t } = useApp()
  const [profileOpen, setProfileOpen] = useState(false)
  const [name, setName] = useState<string>('')

  useEffect(() => {
    void userManager.getUser().then((oidcUser) => {
      const profile = oidcUser?.profile
      setName(String(profile?.name ?? profile?.preferred_username ?? ''))
    })
  }, [])

  // Число непрочитанных — опросом раз в минуту, а не вторым потоком SSE: поток держит
  // экран уведомлений, и два соединения на вкладку — это два эмиттера на сервере.
  const mayNotify = can(user, 'system.notification', 'read')
  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    enabled: mayNotify,
    queryFn: ({ signal }) => fetchNotifications(true, signal).then((page) => page.unread),
    refetchInterval: 60_000,
    retry: false,
  })

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '·'

  const themes: { value: ThemePreference; icon: 'sun' | 'moon' | 'monitor'; label: string }[] = [
    { value: 'light', icon: 'sun', label: t('theme.light') },
    { value: 'dark', icon: 'moon', label: t('theme.dark') },
    { value: 'system', icon: 'monitor', label: t('theme.system') },
  ]

  const themeSwitch = (
    <div className="seg seg-icon" role="group" aria-label={t('topbar.theme')}>
      {themes.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={theme === option.value}
          aria-label={option.label}
          title={option.label}
          onClick={() => setTheme(option.value)}
        >
          <Icon name={option.icon} size="sm" />
        </button>
      ))}
    </div>
  )
  const localeSwitch = (
    <button
      className="btn btn-ghost btn-sm"
      type="button"
      aria-label={t('topbar.locale')}
      onClick={() => setLocale(locale === 'ru' ? 'en' : 'ru')}
    >
      {locale === 'ru' ? 'RU' : 'EN'}
    </button>
  )

  return (
    <header className="topbar">
      {narrow && (
        <button
          ref={menuButton}
          className="btn btn-ghost btn-icon nav-toggle"
          type="button"
          aria-label={t(menuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
          aria-expanded={menuOpen}
          aria-controls="nav-drawer"
          onClick={onMenu}
        >
          <Icon name={menuOpen ? 'close' : 'lines'} />
        </button>
      )}
      <button className="search-trigger" type="button" aria-label={t('topbar.searchLabel')} onClick={openPalette}>
        <Icon name="search" />
        <span className="ellipsis">{t('topbar.search')}</span>
        <span className="kbd">Ctrl K</span>
      </button>
      <div className="spacer" />
      {mayNotify && (
        <NavLink
          to="/notifications"
          className="btn btn-ghost btn-icon bell"
          aria-label={t('topbar.notifications', { count: unread.data ?? 0 })}
        >
          <Icon name="bell" />
          {(unread.data ?? 0) > 0 && <span className="badge-n">{unread.data}</span>}
        </NavLink>
      )}
      {/* On a phone the bar has room for the menu, search, bell and avatar only; theme and
          language move into the profile menu, one tap away. */}
      {!narrow && themeSwitch}
      {!narrow && localeSwitch}
      <div style={{ position: 'relative' }}>
        <button
          className="profile"
          type="button"
          aria-haspopup="menu"
          aria-expanded={profileOpen}
          aria-label={narrow ? (name || t('topbar.profile')) : undefined}
          onClick={() => setProfileOpen((was) => !was)}
        >
          <span className="ava ava-lg ava-me" aria-hidden="true">{initials}</span>
          {!narrow && (
            <span className="stack" style={{ gap: 0 }}>
              <span className="t-sm strong">{name || t('topbar.profile')}</span>
              <span className="t-xs muted">{user?.roles.join(', ')}</span>
            </span>
          )}
          <Icon name="chevron" size="xs" className="muted" />
        </button>
        {profileOpen && (
          <div className="pop" role="menu" style={{ position: 'absolute', right: 0, top: 40, zIndex: 30 }}>
            {narrow && <div className="profile-prefs">{themeSwitch}{localeSwitch}</div>}
            <button className="menu-item danger" type="button" role="menuitem" onClick={onSignOut}>
              <Icon name="logout" />
              {t('auth.signOut')}
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
